import React from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatIDR, formatDate } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const STATUS = {
  reported: "Dilaporkan", awaiting_inspection: "Menunggu Inspeksi",
  awaiting_parts: "Menunggu Sparepart", in_repair: "Sedang Diperbaiki",
  testing: "Testing", completed: "Selesai", unrepairable: "Tidak Bisa Diperbaiki",
};

export default function MaintenancePage() {
  const [rows, setRows] = React.useState([]);
  const [assets, setAssets] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ asset_id: "", problem: "", type: "repair", estimated_cost: 0, notes: "" });

  const load = React.useCallback(async () => {
    const [m, a] = await Promise.all([api.get("/maintenance"), api.get("/assets")]);
    setRows(m.data); setAssets(a.data);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await api.post("/maintenance", form); toast.success("Maintenance dicatat"); setOpen(false); setForm({ asset_id: "", problem: "", type: "repair", estimated_cost: 0, notes: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (mid, status) => {
    try { await api.patch(`/maintenance/${mid}`, { status }); toast.success("Diperbarui"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="maintenance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Maintenance</h1>
          <p className="text-sm text-neutral-500">Catatan perawatan & perbaikan equipment.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-green-600 hover:bg-green-700" data-testid="btn-new-maintenance">Tambah</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Maintenance Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Aset</Label>
                <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })}>
                  <option value="">— pilih —</option>
                  {assets.map((a) => <option key={a.id} value={a.id}>{a.asset_code}</option>)}
                </select>
              </div>
              <div><Label>Masalah</Label><Textarea rows={2} value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Jenis</Label>
                  <select className="w-full border border-neutral-200 rounded-md px-3 py-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="repair">Perbaikan</option>
                    <option value="preventive">Perawatan Berkala</option>
                    <option value="cleaning">Pembersihan</option>
                    <option value="calibration">Kalibrasi</option>
                  </select>
                </div>
                <div><Label>Estimasi Biaya</Label><Input type="number" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: +e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-green-600 hover:bg-green-700" data-testid="save-maintenance">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr>
            <th className="text-left">No.</th>
            <th className="text-left">Aset</th>
            <th className="text-left">Masalah</th>
            <th className="text-left">Jenis</th>
            <th className="text-right">Est. Biaya</th>
            <th className="text-left">Status</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rows.map((m) => {
              const a = assets.find((x) => x.id === m.asset_id);
              return (
                <tr key={m.id} data-testid={`maint-row-${m.id}`}>
                  <td className="font-mono text-xs">{m.number}</td>
                  <td className="text-xs">{a?.asset_code || m.asset_id.slice(0, 8)}</td>
                  <td className="text-xs">{m.problem}</td>
                  <td className="text-xs">{m.type}</td>
                  <td className="text-right">{formatIDR(m.estimated_cost)}</td>
                  <td>
                    <select className="text-xs border border-neutral-200 rounded px-2 py-1 bg-white"
                      value={m.status} onChange={(e) => setStatus(m.id, e.target.value)} data-testid={`maint-status-${m.id}`}>
                      {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="text-xs text-neutral-500">{formatDate(m.created_at)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-neutral-500">Belum ada maintenance.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
